import type { Game } from '../../core/game';
import type { ThrowResult } from '../../core/types';
import { combatLog, throwDetail, type CombatThrow, type ThrowDetail } from '../combatApi';
import { corruptedFactor, dieLevelOf, levelFactorOf } from '../abyssApi';
import { formatDamage, formatGold, formatLevelFactor, formatNumber, formatSeconds } from '../format';
import { triggeredSpellText, triggeredSpells } from '../spellsApi';

/**
 * « Récapitulatif des dégâts » — COMMENTS v0.3.0 §2.
 *
 * Affiché à la fin de **chaque** Expédition et de **chaque** Invasion
 * (`expeditionEnd`, `invasionWon`, `runLost`). Section repliable :
 *  1. un en-tête de métriques (lancers, moyenne, min, max, total, DPS,
 *     critiques, sorts) ;
 *  2. la liste des lancers, **du plus récent au plus ancien**, en lignes
 *     compactes `#n · t=12,3 s · Squelette · 14,5 dégâts · [CRIT] [Étincelle]` ;
 *  3. au tap sur une ligne, le détail du calcul à la granularité de
 *     COMMENTS §1.2.5 (D_L, D_C, D_S, M_P, M_T, affixes, Total) et la liste des
 *     faces de chaque dé.
 *
 * La source est `state.combat.throws`, livrée par l'agent `src/core` en
 * parallèle : sans elle, la section affiche « Historique indisponible ».
 */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** « 12,3 s » sans espace insécable parasite (les lignes sont déjà denses). */
function atLabel(seconds: number): string {
  return `t=${formatNumber(seconds, 1)} s`;
}

/** Ligne « nom : valeur » de l'en-tête de métriques. */
function metric(label: string, value: string, role?: string): HTMLElement {
  const item = el('div', 'recap-metric');
  item.append(el('dt', 'recap-metric-label', label), el('dd', 'recap-metric-value', value));
  if (role) item.setAttribute('data-metric', role);
  return item;
}

interface Metrics {
  count: number;
  mean: number;
  min: number;
  max: number;
  total: number;
  dps: number;
  crits: number;
  spells: number;
  duration: number;
}

function computeMetrics(throws: CombatThrow[], duration: number): Metrics {
  const totals = throws.map((t) => t.result.total);
  const total = totals.reduce((s, n) => s + n, 0);
  return {
    count: throws.length,
    mean: throws.length > 0 ? total / throws.length : 0,
    min: throws.length > 0 ? Math.min(...totals) : 0,
    max: throws.length > 0 ? Math.max(...totals) : 0,
    total,
    dps: duration > 0 ? total / duration : 0,
    crits: throws.filter((t) => t.result.crit === true).length,
    spells: throws.filter((t) => triggeredSpells(t.result).length > 0).length,
    duration,
  };
}

function buildMetrics(m: Metrics): HTMLElement {
  const list = el('dl', 'recap-metrics');
  list.setAttribute('data-role', 'recap-metrics');
  list.append(
    metric('Lancers', String(m.count), 'count'),
    metric('Moyenne', formatDamage(m.mean), 'mean'),
    metric('Min', formatDamage(m.min), 'min'),
    metric('Max', formatDamage(m.max), 'max'),
    metric('Total', formatDamage(m.total), 'total'),
    metric('Dégâts/seconde', formatDamage(m.dps), 'dps'),
    metric('Critiques', String(m.crits), 'crits'),
    metric('Sorts', String(m.spells), 'spells'),
  );
  return list;
}

/** « faces Maîtres (3+2) » — une parenthèse vide serait illisible. */
function facesText(faces: number[]): string {
  return faces.length > 0 ? faces.join('+') : '—';
}

/** Ligne `D_L = faces Maîtres (…) + faces Basiques (…) + plats (…) = X`. */
function lineDL(d: ThrowDetail): string {
  const parts = [
    `faces Maîtres (${facesText(d.masterFaces)})`,
    `faces Basiques (${facesText(d.basicFaces)})`,
    `plats (${formatNumber(d.flat, 1)})`,
  ];
  return `D_L = ${parts.join(' + ')} = ${formatNumber(d.DL, 1)}`;
}

/** Ligne `D_C = (1,5 + 0,05) × (1,2) = Y`, ou « pas de critique ». */
function lineDC(d: ThrowDetail): string {
  if (!d.crit) return 'D_C = 1 (pas de critique)';
  const additive =
    d.critAdditive.length > 0
      ? `(${d.critAdditive.map((n) => formatNumber(n)).join(' + ')})`
      : `(${formatNumber(d.DC)})`;
  const mult =
    d.critMultipliers.length > 0
      ? ` × (${d.critMultipliers.map((n) => formatNumber(n)).join(' × ')})`
      : '';
  return `D_C = ${additive}${mult} = ${formatNumber(d.DC)}`;
}

/**
 * Ligne `D_S = 1,25 × 2 × 1,1 × 1,12 = Z`, ou « pas de sort ».
 *
 * v0.5.0 : plusieurs sorts peuvent tomber sur le même lancer (DECISIONS §E A9)
 * — la ligne liste **tous** les multiplicateurs de sorts, puis les facteurs
 * d'effets (`Puissance des sorts`) fournis par le cœur dans
 * `breakdown.spellEffectMultipliers`, et le détail des sorts suit juste après.
 */
function lineDS(d: ThrowDetail): string {
  if (d.spells.length === 0) return 'D_S = 1 (pas de sort)';
  // Les facteurs neutres (×1) sont tus : un sort de secondes pures (« 42 ») ou
  // une Puissance des sorts à 0 remplirait la ligne de « × 1 » illisibles.
  const factors = [
    ...d.spells.filter((s) => s.multiplier !== 1).map((s) => formatNumber(s.multiplier)),
    ...d.spellEffectMultipliers.filter((n) => n !== 1).map((n) => formatNumber(n)),
  ];
  const names = d.spells.map((s) => s.label).join(', ');
  if (factors.length === 0) return `D_S = 1 (${names})`;
  return `D_S = ${factors.join(' × ')} = ${formatNumber(d.DS)} (${names})`;
}

/**
 * Détail d'un sort déclenché : « Paire ×1,25 », « 42 +3 s », « Nombre d'or
 * or ×1,61 ». Une ligne par sort, emplacement comme dé.
 */
export function spellLines(d: ThrowDetail): string[] {
  return d.spells.map((s) => `${s.kind === 'die' ? 'Sort de dé' : "Sort d'emplacement"} — ${triggeredSpellText(s)}`);
}

/**
 * Facteurs d'effets de sorts nommés. Le cœur publie `[spellPower]` :
 * *Sorcellerie* (v0.7.0) est **repliée dans** la Puissance des sorts, les deux
 * nœuds étant multiplicatifs entre eux (A21) — le libellé le dit plutôt que de
 * laisser croire que le talent n'a pas d'effet. *Préparation* n'y figure plus
 * (A100-2 : elle renforce chaque sort, voir `prepLines`) ; un autre facteur
 * publié plus tard sortirait sous « Effet de sort 2 » au lieu d'être tu.
 */
function spellFactorLines(d: ThrowDetail): string[] {
  if (d.spells.length === 0 || d.spellEffectMultipliers.length === 0) return [];
  const labels = ['Puissance des sorts × Sorcellerie'];
  return d.spellEffectMultipliers
    .map((n, i) => ({ n, label: labels[i] ?? `Effet de sort ${i + 1}` }))
    .filter((f) => f.n !== 1)
    .map((f) => `${f.label} ×${formatNumber(f.n)}`);
}

/**
 * *Préparation* et Mage Maudit (A100) : la ligne « Préparation ×k » quand le
 * cœur publie un facteur global > 1 (le détail par sort, « ×m → ×m′
 * (Préparation) », est dans `spellLines`), et « Sorts ÷ 2 (Mage Maudit) » quand
 * la part de bonus des sorts est divisée. Rien sans l'un ni l'autre — ni sans le cœur A100.
 */
export function prepLines(d: ThrowDetail): string[] {
  const lines: string[] = [];
  if (d.prepMult > 1) lines.push(`Préparation ×${formatNumber(d.prepMult)}`);
  if (d.spellsHalved) lines.push('Sorts ÷ 2 (Mage Maudit)');
  return lines;
}

/**
 * Facteurs nommés que le cœur publie en plus de la formule historique
 * (v0.7.0 : Larbin ×0,75, *Premiers*, *ENFER*, *Sorcellerie*, *Critique
 * Magique*…). **Tout est affiché**, y compris un nom que cette version ne
 * connaît pas : il sort alors sous son nom brut plutôt que d'être avalé en
 * silence (un facteur invisible rendrait le récapitulatif faux).
 */
function extraFactorLines(d: ThrowDetail): string[] {
  return d.extraFactors
    .filter((f) => f.value !== 1)
    .map((f) => `${f.label} ${f.multiplicative ? `×${formatNumber(f.value)}` : formatNumber(f.value)}`);
}

/**
 * Lignes des Grimoires et des Matériaux (A95), seulement quand le facteur est
 * ≠ 1 ou le report > 0. Les facteurs des Grimoires (compris dans D_S) et de
 * Ferreux (compris dans D_L) sont des **traces** ; « Brisé » entre dans le
 * produit du total ; les maximums du lancer précédent et le report de Silicium
 * disent d'où vient un total qui ne se retrouve pas par le seul produit.
 */
export function materialLines(d: ThrowDetail): string[] {
  const t = d.trace;
  const lines: string[] = [];
  if (t.grimoireNumbers !== 1) lines.push(`Grimoire des nombres ×${formatNumber(t.grimoireNumbers)}`);
  if (t.grimoireFaces !== 1) lines.push(`Face je gagne ×${formatNumber(t.grimoireFaces)}`);
  if (t.ironBasic !== 1) lines.push(`Basiques ×${formatNumber(t.ironBasic)} (Ferreux)`);
  if (t.woodCarry > 0) lines.push('Contenant : max du lancer précédent');
  if (t.broken !== 1) lines.push(`Brisé ×${formatNumber(t.broken)}`);
  if (t.voidCarry > 0) lines.push('Lancer : max du précédent');
  if (t.overkillCarry > 0) lines.push(`Report Silicium +${formatNumber(t.overkillCarry, 1)}`);
  return lines;
}

/**
 * Lignes des nouveautés de l'A98, seulement quand elles ont joué : Basiques
 * doublés par Vingt-dicte, Armure de l'ennemi (÷ 1,05^k), dégâts que la
 * marque de Null accumule (A100) ou libère avec elle, part Mult_Corrupted des dés Corrupted. Le
 * `damageDealt` du lancer sert à retrouver une libération quand le cœur ne la
 * publie pas lui-même (un lancer de Null qui inflige plus que son total).
 */
export function a98Lines(d: ThrowDetail, damageDealt?: number, game?: Game | null): string[] {
  const t = d.a98;
  const lines: string[] = [];
  if (t.basicDoubled) lines.push('Basiques doublés (Vingt-dicte)');
  if (t.armorDivisor > 1) {
    // 1,05^k : on retrouve k pour que la ligne dise le facteur et le nombre de coups.
    const k = Math.round(Math.log(t.armorDivisor) / Math.log(1.05));
    const exact = k >= 1 && Math.abs(Math.pow(1.05, k) - t.armorDivisor) < 1e-6;
    lines.push(
      exact
        ? `Armure ×1/1,05^${k} (÷ ${formatNumber(t.armorDivisor)})`
        : `Armure ×1/${formatNumber(t.armorDivisor)}`,
    );
  }
  // A100 : la marque ne retient plus rien, le lancer frappe et s'y ajoute.
  if (t.nullHeld > 0) {
    lines.push(`Marque : +${formatDamage(t.nullHeld)} accumulés`);
  } else {
    const released =
      t.nullReleased > 0
        ? t.nullReleased
        : d.spells.some((s) => s.id === 'nullMark') && typeof damageDealt === 'number' && damageDealt > d.total + 1e-6
          ? damageDealt - d.total
          : 0;
    if (released > 0) lines.push(`Marque libérée : +${formatDamage(released)}`);
  }
  if (t.corruptedBonus > 0) {
    lines.push(`Dés Corrupted ×${formatNumber(corruptedFactor(game), 0)} : +${formatNumber(t.corruptedBonus, 1)}`);
  }
  return lines;
}

/** Ligne `Total = X × Y × Z × … = T` (les facteurs à 1 restent affichés : c'est la formule). */
export function lineTotal(d: ThrowDetail): string {
  const factors = [
    formatNumber(d.DL, 1),
    formatNumber(d.DC),
    formatNumber(d.DS),
    formatNumber(d.MP),
    formatNumber(d.MT),
  ];
  if (d.affixMult !== 1) factors.push(formatNumber(d.affixMult));
  // A95 : l'ennemi brisé (Osseux) multiplie le total ; les autres traces n'entrent pas dans le produit.
  if (d.trace.broken !== 1) factors.push(formatNumber(d.trace.broken));
  // Seuls les facteurs annoncés comme multiplicateurs entrent dans le produit :
  // une valeur informative publiée par le cœur (un compteur, une durée) serait
  // sinon multipliée à tort, et la ligne ne retomberait plus sur le total.
  for (const f of d.extraFactors) {
    if (f.multiplicative && f.value !== 1) factors.push(formatNumber(f.value));
  }
  return `Total = ${factors.join(' × ')} = ${formatDamage(d.total)}`;
}

/** Une ligne de la liste des dés d'un lancer (A78). */
export interface DiceLine {
  /** `master` : un Maître ; `container` : tous les Basiques ; `absorbed` : un dé absorbé. */
  kind: 'master' | 'container' | 'absorbed';
  text: string;
}

/**
 * Niveau d'un dé Maître et facteur de dégâts correspondant (A94), retrouvés par
 * l'identifiant du dé ; `null` pour un dé inconnu (consommé depuis) ou sans niveau.
 */
export type DieLevelLookup = (dieId: string) => { level: number; factor: number } | null;

/** Recherche de niveau branchée sur l'inventaire de la partie. */
export function dieLevelLookupOf(game: Game): DieLevelLookup {
  return (dieId) => {
    const die = game.state.inventory.find((d) => d.id === dieId);
    if (!die) return null;
    const level = dieLevelOf(die);
    return level > 0 ? { level, factor: levelFactorOf(game, die) } : null;
  };
}

/**
 * Suffixe d'un dé que le cœur marque : « (rebond) » (Caoutchouc), « (fantôme) »
 * (Silicium) — A95 — puis, depuis l'A98, « (copie 42) » (le dé relancé par le
 * sort 42), « (réplique) » (la copie de Réplicant) et « (annulé) » (dé retiré
 * du lancer par un boss). Plusieurs marques se lisent ensemble : « (rebond, réplique) ».
 */
function dieMarker(die: ThrowResult['dice'][number]): string {
  const marks = die as { bounce?: boolean; ghost?: boolean; copyOf?: string; voided?: boolean };
  const out: string[] = [];
  if (marks.ghost === true) out.push('fantôme');
  if (marks.bounce === true) out.push('rebond');
  if (marks.copyOf === '42') out.push('copie 42');
  if (marks.copyOf === 'replicant') out.push('réplique');
  if (marks.voided === true) out.push('annulé');
  return out.length > 0 ? ` (${out.join(', ')})` : '';
}

/**
 * Lignes de la liste des dés d'un lancer (A78) : les **Maîtres** restent
 * détaillés un par un, les dés **Basiques** du contenant tiennent en **une
 * ligne** « Contenant : N dés · somme X ». N compte les Basiques lancés, ceux
 * du tractopelle compris (« dont tractopelle M » quand il y en a) ; X est la
 * somme de leurs dégâts après la division éventuelle (*Mutilant*, Mygale…,
 * `basicDivisor`). Un dé absorbé garde sa ligne barrée et n'entre pas dans la
 * somme ; sans Basique, pas de ligne de contenant.
 */
export function diceLines(result: ThrowResult, levelOf?: DieLevelLookup): DiceLine[] {
  const d = throwDetail(result);
  const lines: DiceLine[] = [];
  let basics = 0;
  let basicSum = 0;
  for (const die of result.dice) {
    if (!die.isMaster && !die.absorbed) {
      basics++;
      basicSum += die.damage;
      continue;
    }
    // Un dé Maître de niveau (A94) : « face 4 · Nv 7 ×17 → 68,3 ».
    const lv = die.isMaster && die.dieId && levelOf ? levelOf(die.dieId) : null;
    const label = [
      `d${die.sides}`,
      lv
        ? `face ${die.face} · Nv ${lv.level} ${formatLevelFactor(lv.factor)} → ${formatNumber(die.face * lv.factor, 1)}`
        : `face ${die.face}`,
      // A95 : le second lancer d'un dé en rebond (Caoutchouc) et le dé fantôme (Silicium).
      `${die.isMaster ? 'Maître' : 'Basique'}${dieMarker(die)}`,
      `${formatDamage(die.damage)} dégâts`,
    ];
    // Un dé annulé par un boss le dit déjà dans son suffixe ; seul le bouclier « absorbe ».
    if (die.absorbed && (die as { voided?: boolean }).voided !== true) label.push('absorbé');
    lines.push({ kind: die.absorbed ? 'absorbed' : 'master', text: label.join(' · ') });
  }
  // Les dés du tractopelle ne sont pas dans `dice` : seule leur somme brute est publiée.
  const backhoe = Math.max(0, Math.floor(d.backhoeDice));
  const count = basics + backhoe;
  if (count > 0) {
    const sum = (basicSum + d.backhoeBasic) / (d.basicDivisor > 0 ? d.basicDivisor : 1);
    const dice = `${formatGold(count)} ${count === 1 ? 'dé' : 'dés'}`;
    const from = backhoe > 0 ? ` (dont tractopelle ${formatGold(backhoe)})` : '';
    lines.push({ kind: 'container', text: `Contenant : ${dice}${from} · somme ${formatNumber(sum, 1)}` });
  }
  return lines;
}

/** Détail de calcul d'un lancer (COMMENTS §1.2.5). */
function buildThrowDetail(t: CombatThrow, levelOf?: DieLevelLookup, game?: Game | null): HTMLElement {
  const d = throwDetail(t.result);
  const wrap = el('div', 'recap-detail');
  wrap.setAttribute('data-role', 'recap-detail');

  const lines = el('ul', 'recap-formula');
  const push = (text: string, cls?: string): void => {
    lines.appendChild(el('li', cls, text));
  };
  if (t.dodged) {
    wrap.appendChild(el('p', 'recap-dodged', "Esquivé : ce lancer n'a eu aucun effet (A29)."));
  }
  push(lineDL(d));
  // A94 : les niveaux des dés relèvent D_L ; la ligne dit de combien.
  if (d.dieLevelBonus > 0) {
    push(`Niveaux des dés : +${formatNumber(d.dieLevelBonus, 1)}`, 'recap-formula-factor');
  }
  // v0.26.0 (A72-17) : les dés du tractopelle ne sont pas listés dé par dé.
  if (d.backhoeDice > 0) {
    push(
      `Tractopelle : +${formatGold(d.backhoeDice)} dés Basiques, faces ${formatNumber(d.backhoeBasic, 0)} (comprises dans D_L)`,
      'recap-formula-factor',
    );
  }
  push(lineDC(d));
  push(lineDS(d));
  for (const line of spellLines(d)) push(line, 'recap-formula-spell');
  for (const line of spellFactorLines(d)) push(line, 'recap-formula-spell');
  // Préparation (A100-2) et Mage Maudit (A100-1).
  for (const line of prepLines(d)) push(line, 'recap-formula-factor');
  for (const line of extraFactorLines(d)) push(line, 'recap-formula-factor');
  // Grimoires et Matériaux (A95).
  for (const line of materialLines(d)) push(line, 'recap-formula-factor');
  // Vingt-dicte, Armure, marque de Null et dés Corrupted (A98).
  for (const line of a98Lines(d, t.damageDealt, game)) push(line, 'recap-formula-factor');
  // Effets d'ennemi sur la formule (v0.8.0, §F) : diviseur critique, diviseur
  // des dés Basiques, emplacements bloqués (« Sorts ÷ 2 » du Mage Maudit : `prepLines`).
  // Neutres (÷1, faux, 0) tus — comme les autres facteurs, une valeur neutre ne dit rien au joueur.
  if (d.critDivisor !== 1) push(`Critique ÷ ${formatNumber(d.critDivisor)} (ennemi)`, 'recap-formula-factor');
  if (d.basicDivisor !== 1) push(`Basiques ÷ ${formatNumber(d.basicDivisor)} (ennemi)`, 'recap-formula-factor');
  if (d.blockedSlots > 0) {
    push(
      `${d.blockedSlots} emplacement${d.blockedSlots > 1 ? 's' : ''} bloqué${d.blockedSlots > 1 ? 's' : ''} (ennemi)`,
      'recap-formula-factor',
    );
  }
  push(`M_P = ${formatNumber(d.MP)}`);
  push(`M_T = ${formatNumber(d.MT)}`);
  if (d.affixMult !== 1) push(`Exécutant ×${formatNumber(d.affixMult)}`);
  push(lineTotal(d), 'recap-formula-total');
  // v0.26.0 : Triche (A72-6) et double coup de Guinsoo (A72-15).
  if (d.cheat) {
    push(
      `Triche : lancer ${d.cheat.kept} gardé sur 2 (l’autre faisait ${formatDamage(d.cheat.discardedTotal)})`,
      'recap-formula-factor',
    );
  }
  if (d.hits === 2) push('Guinsoo : dégâts appliqués deux fois (2 coups)', 'recap-formula-factor');
  wrap.appendChild(lines);

  // Faces dé par dé — le calcul ci-dessus les agrège, la liste les déplie.
  // Depuis la v0.33.0 (A78), les Basiques du contenant tiennent en une ligne.
  const dice = el('ul', 'recap-dice');
  for (const line of diceLines(t.result, levelOf)) {
    const cls = ['recap-die'];
    if (line.kind === 'absorbed') cls.push('recap-die--absorbed');
    if (line.kind === 'container') cls.push('recap-die--container');
    const li = el('li', cls.join(' '), line.text);
    li.setAttribute('data-die', line.kind);
    dice.appendChild(li);
  }
  wrap.appendChild(dice);

  const applied = el(
    'p',
    'recap-applied',
    `Infligés : ${formatDamage(t.damageDealt)}${t.killed ? ' · ennemi tué' : ''}`,
  );
  wrap.appendChild(applied);

  if (!d.exact) {
    wrap.appendChild(
      el(
        'p',
        'recap-approx',
        'Détail reconstitué : le cœur ne fournit pas encore ThrowResult.breakdown.',
      ),
    );
  }
  return wrap;
}

/** Étiquette d'un lancer : badge F1 teinté (`recap-tag--<kind>` reste le crochet des tests). */
function tag(kind: string, tone: string, text: string): HTMLElement {
  const node = el('span', `f-badge recap-tag recap-tag--${kind}`, text);
  node.setAttribute('data-tone', tone);
  return node;
}

/** Une ligne de lancer, dépliable au tap (`<details>` natif). */
function buildThrowRow(t: CombatThrow, index: number, levelOf?: DieLevelLookup, game?: Game | null): HTMLElement {
  const row = el('details', 'recap-throw');
  row.setAttribute('data-role', 'recap-throw');
  row.setAttribute('data-throw', String(t.id));

  const summary = el('summary', 'recap-throw-head');
  const parts = [
    `#${index}`,
    atLabel(t.at),
    t.enemyName,
    `${formatDamage(t.result.total)} dégâts`,
  ];
  summary.appendChild(el('span', 'recap-throw-line num', parts.join(' · ')));

  // Étiquettes = badges F1 (`.f-badge`, liseré de la teinte `data-tone`).
  const tags = el('span', 'recap-tags');
  if (t.dodged) tags.appendChild(tag('dodge', 'dim', 'Esquivé'));
  if (t.result.crit === true) tags.appendChild(tag('crit', 'gold', 'CRIT'));
  if ((t.result as { hits?: number }).hits === 2) tags.appendChild(tag('double', 'gold', '×2 coups'));
  if ((t.result as { backhoe?: number }).backhoe) tags.appendChild(tag('backhoe', 'dim', 'Tractopelle'));
  for (const spell of triggeredSpells(t.result)) {
    tags.appendChild(tag('spell', 'spell', spell.label));
  }
  if (t.killed) tags.appendChild(tag('kill', 'good', 'Tué'));
  if (tags.childElementCount > 0) summary.appendChild(tags);
  row.appendChild(summary);

  // Le détail n'est construit qu'à la première ouverture : une Invasion peut
  // compter des centaines de lancers, et l'écran de fin doit rester instantané.
  let built = false;
  row.addEventListener('toggle', () => {
    if (!row.open || built) return;
    built = true;
    row.appendChild(buildThrowDetail(t, levelOf, game));
  });

  return row;
}

/**
 * Construit la section « Récapitulatif des dégâts ».
 * `open` : dépliée d'entrée (vrai en fin d'Expédition, faux ailleurs).
 */
export function buildDamageRecap(game: Game, opts: { open: boolean }): HTMLElement {
  // Panneau facetté repliable (`.f-details`, refonte F1 lot 3, v0.16.0).
  const section = el('details', 'f-panel f-details recap');
  section.setAttribute('data-role', 'damage-recap');
  section.open = opts.open;
  section.appendChild(el('summary', 'recap-summary', 'Récapitulatif des dégâts'));

  const log = combatLog(game);
  if (!log) {
    section.appendChild(el('p', 'recap-empty', 'Historique indisponible'));
    return section;
  }
  if (log.throws.length === 0) {
    section.appendChild(el('p', 'recap-empty', 'Aucun lancer enregistré.'));
    return section;
  }

  const m = computeMetrics(log.throws, log.durationPlayed);
  section.appendChild(buildMetrics(m));
  section.appendChild(
    el('p', 'recap-duration', `Durée jouée : ${formatSeconds(m.duration)} · ${log.kind === 'invasion' ? 'Invasion' : 'Expédition'}`),
  );

  const list = el('ol', 'recap-list');
  list.setAttribute('data-role', 'recap-list');
  const levelOf = dieLevelLookupOf(game);
  // Du plus récent au plus ancien : le dernier lancer est celui que le joueur
  // vient de voir, c'est lui qu'il cherche en premier.
  for (let i = log.throws.length - 1; i >= 0; i--) {
    const li = el('li', 'recap-row');
    li.appendChild(buildThrowRow(log.throws[i], i + 1, levelOf, game));
    list.appendChild(li);
  }
  section.appendChild(list);
  return section;
}
